/**
 * Map of width presets to CSS classes
 */
export const MODAL_WIDTH_MAP = {
  sm: 'max-w-[360px]',
  md: 'max-w-[500px]',
  lg: 'max-w-[640px]',
  xl: 'max-w-[800px]',
  page: 'max-w-[93.75vw]',
} as const

/**
 * Map of height presets to CSS classes
 */
export const MODAL_HEIGHT_MAP = {
  sm: 'h-[360px]',
  md: 'h-[478px]',
  lg: 'h-[640px]',
  xl: 'h-[800px]',
  full: 'h-[85vh]',
  page: 'h-[90vh]',
  // Pixel `max-*` presets clamp against 85dvh so the preset caps size on tall screens while the
  // viewport wins on short ones. The percentage presets are already viewport-relative, so they use
  // `dvh` (not `vh`) for the same reason: the dynamic viewport unit tracks mobile browser chrome and
  // keeps the dialog from clipping its content on low-height screens.
  'max-sm': 'max-h-[min(360px,85dvh)]',
  'max-md': 'max-h-[min(478px,85dvh)]',
  'max-lg': 'max-h-[min(640px,85dvh)]',
  'max-xl': 'max-h-[min(800px,85dvh)]',
  'max-full': 'max-h-[85dvh]',
  'max-page': 'max-h-[90dvh]',
} as const

export type ModalWidth = keyof typeof MODAL_WIDTH_MAP
export type ModalHeight = keyof typeof MODAL_HEIGHT_MAP

/**
 * Available modal width presets
 */
export const MODAL_WIDTHS = Object.keys(MODAL_WIDTH_MAP) as Array<keyof typeof MODAL_WIDTH_MAP>

/**
 * Available modal height presets (including fixed and max heights)
 */
export const MODAL_HEIGHTS = Object.keys(MODAL_HEIGHT_MAP) as Array<keyof typeof MODAL_HEIGHT_MAP>

/**
 * Fixed height presets (non-scrollable)
 */
export const MODAL_FIXED_HEIGHTS: Array<keyof typeof MODAL_HEIGHT_MAP> = ['sm', 'md', 'lg', 'xl', 'full', 'page']

/**
 * Max height presets (scrollable)
 */
export const MODAL_MAX_HEIGHTS: Array<keyof typeof MODAL_HEIGHT_MAP> = ['max-sm', 'max-md', 'max-lg', 'max-xl', 'max-full', 'max-page']

export enum ModalFooterWithActionsType {
  /** Default type with primary CTA. */
  DEFAULT = 'default',
  /** Destructive type with destructive CTA. */
  DESTRUCTIVE = 'destructive',
}
